import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaClipboardList, FaEye, FaSearch, FaFilter,
  FaArrowLeft, FaTruck, FaMapMarkerAlt, FaWeightHanging,
  FaMoneyBillWave, FaCalendarAlt, FaCheckCircle, FaClock,
  FaHistory
} from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import { jobUnits, rateSuffix } from '../utils/jobUnits';

function ClientJobs({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [user, setUser] = useState({});
  const [clientId, setClientId] = useState(null);
  const [loads, setLoads] = useState({});

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchClientData(userData);
  }, []);

  const fetchClientData = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/clients?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.clients && data.clients.length > 0) {
        const id = data.clients[0].id;
        setClientId(id);
        await fetchJobs(id);
        await fetchLoads(id);
      }
    } catch (err) {
      console.error('Error fetching client data:', err);
    } finally {
      // Never leave the page spinning: if there is no client record, the empty
      // states below are shown instead.
      setLoading(false);
    }
  };

  const fetchJobs = async (id) => {
    try {
      const response = await fetch(`/jobs/client/${id}`);
      const data = await response.json();
      if (data.success) {
        setJobs(data.jobs || []);
        setFilteredJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLoads = async (id) => {
    try {
      const response = await fetch(`/client/loads?client_id=${id}&include_completed=true`);
      const data = await response.json();
      if (data.success) {
        // Group loads by job_id
        const loadsByJob = {};
        (data.loads || []).forEach(load => {
          if (!loadsByJob[load.job_id]) {
            loadsByJob[load.job_id] = [];
          }
          loadsByJob[load.job_id].push(load);
        });
        setLoads(loadsByJob);
      }
    } catch (err) {
      console.error('Error fetching loads for jobs:', err);
    }
  };

  useEffect(() => {
    let filtered = jobs;
    
    if (searchTerm) {
      filtered = filtered.filter(j => 
        j.job_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        j.commodity?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        j.origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        j.destination?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(j => j.status === filterStatus);
    }
    
    if (!showCompleted) {
      filtered = filtered.filter(j => j.status !== 'completed' && j.status !== 'cancelled');
    }
    
    setFilteredJobs(filtered);
  }, [searchTerm, filterStatus, showCompleted, jobs]);

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'new': 'bg-yellow-100 text-yellow-600',
      'active': 'bg-blue-100 text-blue-600',
      'in_progress': 'bg-blue-100 text-blue-600',
      'completed': 'bg-green-100 text-green-600',
      'cancelled': 'bg-red-100 text-red-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending Review',
      'new': 'New Request',
      'active': 'In Progress',
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const getJobProgress = (job) => {
    const jobLoads = loads[job.id] || [];
    const completed = jobLoads.filter(l => l.status === 'completed' || l.status === 'delivered').length;
    const total = jobLoads.length;
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const handleViewJob = (jobId) => {
    navigate(`/client-job-details/${jobId}`);
  };

  if (loading) {
    return (
      <ClientLayout activeTab="jobs" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading your jobs...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout activeTab="jobs" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            🚛 My Jobs
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>
            {filteredJobs.length} jobs • {jobs.filter(j => j.status !== 'completed' && j.status !== 'cancelled').length} active
          </p>
        </div>
        <Link
          to="/client-new-job"
          className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
          style={{ backgroundColor: '#00A86B' }}
        >
          + New Job Request
        </Link>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-xl border p-4 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
            </div>
          </div>
          <div className="w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm" style={{ color: '#4A5568' }}>
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            Show Completed
          </label>
        </div>
      </div>

      {/* Jobs List */}
      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold mb-2" style={{ color: '#0A1929' }}>No Jobs Found</h3>
          <p className="text-sm" style={{ color: '#4A5568' }}>No jobs match your current filters.</p>
          <Link
            to="/client-new-job"
            className="mt-4 inline-block px-6 py-2 rounded-lg text-white text-sm font-medium transition-colors"
            style={{ backgroundColor: '#00A86B' }}
          >
            Create Job Request
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredJobs.map((job) => {
            const progress = getJobProgress(job);
            const units = jobUnits(job);
            const jobLoads = loads[job.id] || [];
            const completedCount = jobLoads.filter(l => l.status === 'completed' || l.status === 'delivered').length;
            
            return (
              <div key={job.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold" style={{ color: '#0A1929' }}>{job.job_number}</h3>
                      <span className={getStatusBadge(job.status)}>
                        {getStatusLabel(job.status)}
                      </span>
                      {job.status === 'completed' && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <FaCheckCircle size={12} /> Done
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm" style={{ color: '#4A5568' }}>
                      {units.kind !== 'none' && (
                        <span className="flex items-center gap-1">
                          <FaWeightHanging size={14} /> {units.kind === 'container' ? units.short : `${units.total} MT`}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <FaMapMarkerAlt size={14} /> {job.origin} → {job.destination}
                      </span>
                      <span className="flex items-center gap-1">
                        <FaMoneyBillWave size={14} /> R{job.client_rate || 0}{rateSuffix(units)}
                      </span>
                      <span className="flex items-center gap-1">
                        <FaCalendarAlt size={14} /> {job.start_date || 'TBD'}
                      </span>
                      {jobLoads.length > 0 && (
                        <span className="flex items-center gap-1 text-green-600">
                          <FaTruck size={14} /> {completedCount}/{jobLoads.length} loads done
                        </span>
                      )}
                    </div>
                    {(job.purchase_order_number || job.purchase_order_url) && (
                      <div className="mt-3 rounded-lg px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1" style={{ backgroundColor: '#F0FDF9' }}>
                        {job.purchase_order_number && (
                          <span className="text-sm" style={{ color: '#0A1929' }}>Your P.O.: <strong>{job.purchase_order_number}</strong></span>
                        )}
                        {job.purchase_order_url && (
                          <a href={job.purchase_order_url} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline" style={{ color: '#00A86B' }}>
                            View attached P.O.{job.purchase_order_name ? ` (${job.purchase_order_name})` : ''}
                          </a>
                        )}
                        {job.terms_accepted_at && (
                          <span className="text-xs" style={{ color: '#4A5568' }}>
                            Terms accepted {new Date(job.terms_accepted_at).toLocaleDateString('en-ZA')}{job.terms_accepted_name ? ` by ${job.terms_accepted_name}` : ''}
                          </span>
                        )}
                      </div>
                    )}

                    {job.client_notes && (
                      <p className="mt-2 text-sm" style={{ color: '#4A5568' }}>
                        <span className="font-medium">Notes:</span> {job.client_notes}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleViewJob(job.id)}
                      className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:border-green hover:text-green transition-colors"
                      style={{ borderColor: '#E8ECEF' }}
                    >
                      <FaEye size={14} /> View
                    </button>
                  </div>
                </div>

                {/* Progress Bar */}
                {jobLoads.length > 0 && (
                  <div className="mt-4 pt-3 border-t" style={{ borderColor: '#E8ECEF' }}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs" style={{ color: '#4A5568' }}>Load Progress</span>
                      <span className="text-xs font-medium" style={{ color: '#00A86B' }}>{progress}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${progress}%`, backgroundColor: progress >= 100 ? '#047857' : '#00A86B' }}
                      />
                    </div>
                    <div className="flex justify-between text-xs mt-1" style={{ color: '#4A5568' }}>
                      <span>{completedCount} completed</span>
                      <span>{jobLoads.length} total loads</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </ClientLayout>
  );
}

export default ClientJobs;
